import React, { useEffect } from 'react';

interface SeoProps {
  title: string;
  description: string;
  path: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  lang?: string;
  imageUrl?: string;
}

const upsertMeta = (key: string, content: string, attr: 'name' | 'property' = 'name') => {
  let element = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertCanonical = (href: string) => {
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = href;
};

const upsertAlternate = (hreflang: string, href: string) => {
  const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = 'alternate';
    element.setAttribute('hreflang', hreflang);
    document.head.appendChild(element);
  }
  element.href = href;
};

const Seo: React.FC<SeoProps> = ({ title, description, path, jsonLd, lang = 'ar', imageUrl }) => {
  const json = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const url = `https://alshiraa-customs.com${path === '/' ? '/' : path}`;
    const image = imageUrl
      ? imageUrl.startsWith('http')
        ? imageUrl
        : `https://alshiraa-customs.com${imageUrl}`
      : 'https://alshiraa-customs.com/og-image.png';
    document.documentElement.lang = lang;
    document.title = title;
    upsertMeta('description', description);
    upsertMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    upsertMeta('og:type', 'website', 'property');
    upsertMeta('og:locale', lang === 'ar' ? 'ar_SY' : lang === 'de' ? 'de_DE' : 'en_US', 'property');
    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:url', url, 'property');
    upsertMeta('og:site_name', 'الشراع للتخليص الجمركي', 'property');
    upsertMeta('og:image', image, 'property');
    upsertMeta('og:image:alt', 'شعار شركة الشراع للتخليص الجمركي', 'property');
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', image);
    upsertCanonical(url);
    upsertAlternate('ar', url);
    upsertAlternate('x-default', url);

    const existing = document.getElementById('page-jsonld');
    if (json) {
      const script = existing || document.createElement('script');
      script.id = 'page-jsonld';
      script.setAttribute('type', 'application/ld+json');
      script.textContent = json;
      if (!existing) {
        document.head.appendChild(script);
      }
    } else if (existing) {
      existing.remove();
    }
  }, [title, description, path, json, lang, imageUrl]);

  return null;
};

export default Seo;
